import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmDialog,
  EmptyState,
  FullscreenSpinner,
} from '@shared/ui';
import { userApi, type KycStatus } from '@entities/user';
import { auditApi } from '@entities/audit';
import { formatDateTime, initials } from '@shared/lib/format';
import { useBlockUser, useUnblockUser } from '@features/user-moderation';

const kycVariant: Record<KycStatus, 'secondary' | 'warning' | 'success' | 'destructive'> = {
  NONE: 'secondary',
  PENDING: 'warning',
  VERIFIED: 'success',
  REJECTED: 'destructive',
};

export function UserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [blockOpen, setBlockOpen] = useState(false);
  const [unblockOpen, setUnblockOpen] = useState(false);

  const user = useQuery({
    queryKey: ['user', id],
    queryFn: () => userApi.getBasic(id),
    enabled: !!id,
  });

  const actorAudit = useQuery({
    queryKey: ['audit', { actor: id }],
    queryFn: () => auditApi.list({ actor: id, size: 30 }),
    enabled: !!id,
  });

  const targetAudit = useQuery({
    queryKey: ['audit', { targetType: 'User', targetId: id }],
    queryFn: () => auditApi.list({ targetType: 'User', targetId: id, size: 30 }),
    enabled: !!id,
  });

  const block = useBlockUser(id);
  const unblock = useUnblockUser(id);

  if (user.isLoading) return <FullscreenSpinner />;
  if (user.isError || !user.data) return <EmptyState title="Пользователь не найден" />;

  const u = user.data;

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-lg font-semibold text-primary">
            {initials(u.displayName)}
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{u.displayName}</h1>
            <div className="text-sm text-muted-foreground">{u.email ?? '—'}</div>
          </div>
        </div>

        {u.blocked ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Пользователь заблокирован
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Профиль</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Роли</dt>
              <dd className="flex flex-wrap gap-1">
                {u.roles.map((r) => (
                  <Badge key={r} variant="outline">
                    {r}
                  </Badge>
                ))}
              </dd>
              <dt className="text-muted-foreground">KYC</dt>
              <dd>
                <Badge variant={kycVariant[u.kycStatus]}>{u.kycStatus}</Badge>
              </dd>
              <dt className="text-muted-foreground">Active</dt>
              <dd>{u.active ? '✓' : '—'}</dd>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Действия пользователя (последние)</CardTitle>
          </CardHeader>
          <CardContent>
            <AuditList entries={actorAudit.data?.content} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Действия над пользователем</CardTitle>
          </CardHeader>
          <CardContent>
            <AuditList entries={targetAudit.data?.content} />
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Модерация</CardTitle>
          </CardHeader>
          <CardContent>
            {u.blocked ? (
              <Button variant="success" className="w-full" onClick={() => setUnblockOpen(true)}>
                Unblock
              </Button>
            ) : (
              <Button variant="destructive" className="w-full" onClick={() => setBlockOpen(true)}>
                Block
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={blockOpen}
        onOpenChange={setBlockOpen}
        title="Заблокировать пользователя?"
        description="Чаты, заказы и каталог производителя будут затронуты автоматически. Пользователь получит email."
        confirmLabel="Block"
        variant="destructive"
        withReason
        reasonLabel="Причина блокировки"
        isPending={block.isPending}
        onConfirm={(reason) => block.mutate(reason, { onSuccess: () => setBlockOpen(false) })}
      />
      <ConfirmDialog
        open={unblockOpen}
        onOpenChange={setUnblockOpen}
        title="Разблокировать пользователя?"
        confirmLabel="Unblock"
        variant="success"
        isPending={unblock.isPending}
        onConfirm={() => unblock.mutate(undefined, { onSuccess: () => setUnblockOpen(false) })}
      />
    </div>
  );
}

function AuditList({ entries }: { entries: { id: string; action: string; at: string }[] | undefined }) {
  if (!entries?.length) {
    return <p className="text-sm text-muted-foreground">Записей нет</p>;
  }
  return (
    <ul className="divide-y">
      {entries.map((e) => (
        <li key={e.id} className="flex items-center justify-between py-1.5 text-sm">
          <Badge variant="outline" className="font-mono">
            {e.action}
          </Badge>
          <time className="text-xs text-muted-foreground">{formatDateTime(e.at)}</time>
        </li>
      ))}
    </ul>
  );
}
